import { useState, useEffect, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { Skeleton } from "@/components/ui/skeleton";
import { ProposalHero } from "@/components/proposal/hero";
import {
  ProposalCalculatorSection,
  ProposalAcceptedNotice,
  type CalculatorState,
  type ProductCatalogItem,
} from "@/components/proposal/calculator";
import { ProposalFeedbackSection } from "@/components/proposal/feedback";
import { ProposalOpenQuestionsSection } from "@/components/proposal/open-questions";
import { ProposalFooter } from "@/components/proposal/footer";
import {
  AcceptProposalDialog,
  AskQuestionDialog,
} from "@/components/proposal/dialogs";
import { NoiumWordmark } from "@/components/proposal/shared";
import {
  ProposalLandingState,
  type LandingStateKind,
} from "@/components/proposal/landing-states";
import {
  BODY_SECTION_IDS,
  BODY_SECTION_COMPONENTS,
  hasBodyData,
  type ProposalBodyData,
  type BodySectionId,
} from "@/components/proposal/body-sections";
import {
  BRAND_SECTION_IDS,
  BRAND_SECTION_COMPONENTS,
  type BrandSectionId,
} from "@/components/proposal/brand-sections";
import { getProposalTexts, type ProposalLanguage } from "@/components/proposal/i18n";
import { downloadQuotePdf, generateQuotePdfBase64 } from "@/lib/quote-document";

// The body sections (pain points, scope, outcomes…) are written by a 30–60s
// background AI call that runs after the proposal row already exists, so a
// freshly generated page can load with an empty shell. We poll until the body
// shows up, then stop. The max cap stops a proposal that legitimately has no
// body from refetching forever.
const BODY_POLL_INTERVAL_MS = 4000;
const BODY_POLL_MAX_MS = 3 * 60 * 1000;
const BODY_CONTENT_KEYS = [
  "pain_points",
  "needs",
  "scope",
  "how_we_work",
  "outcomes",
  "timeline",
  "why_us",
  "case_studies",
  "recommended",
  "next_steps",
] as const;

function hasGeneratedBody(variables: Record<string, unknown> | null | undefined): boolean {
  if (!variables) return false;
  return BODY_CONTENT_KEYS.some((key) => {
    const v = variables[key];
    if (v == null) return false;
    if (typeof v === "string") return v.trim().length > 0;
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === "object") return Object.keys(v as object).length > 0;
    return true;
  });
}

export function PublicProposalPage() {
  const { slug } = useParams();
  const [acceptOpen, setAcceptOpen] = useState(false);
  const [questionOpen, setQuestionOpen] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [calcState, setCalcState] = useState<CalculatorState | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const proposal = trpc.public.proposals.get.useQuery(
    { slug: slug! },
    { enabled: !!slug }
  );

  // A freshly generated page can load before the 30–60s background AI body
  // call finishes, leaving the sections empty. Poll a side-effect-free status
  // endpoint (the real `get` records a view on every call, so we must not poll
  // it) until the body lands, then refetch the page once. Capped so a proposal
  // that legitimately has no body doesn't poll forever.
  const loadedBodyEmpty =
    !!proposal.data &&
    !hasGeneratedBody(
      (proposal.data as { variables?: Record<string, unknown> | null }).variables
    );
  const pollStartRef = useRef<number>(Date.now());
  const bodyStatus = trpc.public.proposals.bodyStatus.useQuery(
    { slug: slug! },
    {
      enabled: !!slug && loadedBodyEmpty,
      refetchInterval: (q) => {
        if (Date.now() - pollStartRef.current > BODY_POLL_MAX_MS) return false;
        const data = q.state.data as { bodyReady?: boolean } | undefined;
        if (data?.bodyReady) return false;
        return BODY_POLL_INTERVAL_MS;
      },
    }
  );
  const refetchProposal = proposal.refetch;
  useEffect(() => {
    if (loadedBodyEmpty && bodyStatus.data?.bodyReady) {
      refetchProposal();
    }
  }, [loadedBodyEmpty, bodyStatus.data?.bodyReady, refetchProposal]);
  const token = (proposal.data as { publicToken?: string } | undefined)?.publicToken;

  const language = ((proposal.data as { language?: ProposalLanguage | null } | undefined)?.language ?? "lt") as ProposalLanguage;
  const tx = getProposalTexts(language);

  const acceptProposal = trpc.public.proposals.accept.useMutation({
    onSuccess: () => {
      setAccepted(true);
      toast.success(tx.toasts.accepted);
    },
    onError: (e) => toast.error(e.message ?? tx.toasts.genericError),
  });
  const askQuestion = trpc.public.proposals.askQuestion.useMutation({
    onSuccess: () => {
      setQuestionOpen(false);
      toast.success(tx.toasts.questionSent);
    },
    onError: (e) => toast.error(e.message ?? tx.toasts.genericError),
  });
  const recordEvent = trpc.public.proposals.recordEvent.useMutation();
  const recordExtraInterest = trpc.public.proposals.recordExtraInterest.useMutation();
  const submitFeedback = trpc.public.proposals.submitFeedback.useMutation({
    onSuccess: () => {
      toast.success(tx.toasts.feedbackThanks);
    },
    onError: (e) => toast.error(e.message ?? tx.toasts.feedbackFailed),
  });

  useEffect(() => {
    if (proposal.data?.calculatorState) {
      setCalcState(proposal.data.calculatorState as CalculatorState);
    }
  }, [proposal.data?.calculatorState]);

  // Snapshot which extras the admin pinned at proposal generation. These get
  // promoted into the main quote on the customer-facing page. Customer-side
  // toggles only change the local calcState and never mutate this set, so
  // adding/removing toggles in the addons list doesn't reshuffle the layout.
  const pinnedExtraIds = useMemo<Set<string>>(() => {
    const cs = proposal.data?.calculatorState as CalculatorState | undefined;
    if (!cs) return new Set();
    return new Set(
      (cs.extra ?? [])
        .filter((p) => p.selected)
        .map((p) => p.stripe_product_id)
    );
  }, [proposal.data?.calculatorState]);

  const handleCalcChange = (newState: CalculatorState) => {
    setCalcState(newState);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (!token) return;
      recordEvent.mutate({
        token,
        eventType: "calc_changed",
        payload: { calculatorState: newState },
      });
    }, 1500);
  };

  if (proposal.isLoading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="mx-auto max-w-3xl px-6 py-16 space-y-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (!proposal.data) {
    return (
      <div className="min-h-screen bg-white text-foreground">
        <div className="mx-auto max-w-3xl px-6 py-24">
          <NoiumWordmark />
          <h1 className="mt-12 font-display text-4xl font-bold tracking-display">
            {tx.notFound.title}
          </h1>
          <p className="mt-3 text-muted-foreground">
            {tx.notFound.body}
          </p>
        </div>
      </div>
    );
  }

  const p = proposal.data;
  const isAccepted = accepted || p.status === "accepted";
  const landingFeatures =
    ((p as { landingFeatures?: { feedbackForm?: boolean } | null }).landingFeatures) ?? {};
  const feedbackFormEnabled = landingFeatures.feedbackForm !== false;
  const variables = (p.variables as Record<string, unknown> | null) ?? {};
  const client = (variables.client as Record<string, string> | undefined) ?? {};
  const preparer = (variables.preparer as Record<string, string> | undefined) ?? {};
  const quoteNumber =
    (variables.quoteNumber as string | undefined) ??
    (variables.quote_number as string | undefined) ??
    null;
  const createdAt = (p as { createdAt?: string | Date | null }).createdAt ?? null;
  const expiresAt = (p as { expiresAt?: string | Date | null }).expiresAt ?? null;
  const formatYmd = (d: Date) => {
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, "0");
    const dd = String(d.getUTCDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
  };
  const preparedAt = (() => {
    const base = createdAt ? new Date(createdAt) : null;
    if (!base || Number.isNaN(base.getTime())) return null;
    return formatYmd(base);
  })();
  // An explicit expiresAt (set by an admin from the quote builder) always
  // wins. Otherwise fall back to the legacy implicit rule: 30 days from
  // creation.
  const validUntil = (() => {
    const explicit = expiresAt ? new Date(expiresAt) : null;
    if (explicit && !Number.isNaN(explicit.getTime())) return formatYmd(explicit);
    const base = createdAt ? new Date(createdAt) : null;
    if (!base || Number.isNaN(base.getTime())) return null;
    const expiry = new Date(base);
    expiry.setUTCDate(expiry.getUTCDate() + 30);
    return formatYmd(expiry);
  })();
  const clientContacts =
    (p as {
      clientContacts?: {
        name: string | null;
        jobTitle: string | null;
        email: string | null;
      }[];
    }).clientContacts ?? [];
  const clientCompany =
    (client.company as string | undefined) ??
    (variables.clientCompany as string | undefined) ??
    null;
  const clientPerson =
    (client.name as string | undefined) ??
    (client.person as string | undefined) ??
    null;
  const preparerName =
    (preparer.name as string | undefined) ??
    (variables.preparedBy as string | undefined) ??
    "Noium team";
  const preparerEmail =
    (preparer.email as string | undefined) ??
    (variables.preparedEmail as string | undefined) ??
    "sales@noium.com";

  // Once an opportunity is closed (won/lost) or its quote has expired, the same
  // public /p/:slug link should stop showing the live calculator and instead
  // render a branded "closed" landing page. Won/lost come straight from the
  // opportunity status; expired is derived from the 30-day validUntil window
  // and only applies while the deal is still open.
  const oppStatus = (p as { opportunityStatus?: string | null }).opportunityStatus ?? null;
  const isExpired = (() => {
    if (oppStatus === "won" || oppStatus === "lost") return false;
    if (isAccepted) return false;
    if (!validUntil) return false;
    const today = formatYmd(new Date());
    return validUntil < today;
  })();
  const landingState: LandingStateKind | null =
    oppStatus === "won"
      ? "won"
      : oppStatus === "lost"
        ? "lost"
        : isExpired
          ? "expired"
          : null;
  if (landingState) {
    return (
      <ProposalLandingState
        kind={landingState}
        language={language}
        clientCompany={clientCompany}
      />
    );
  }

  const visibleSections = (p as { visibleSections?: string[] | null }).visibleSections;
  // Proposals created before the section-toggle feature have no visibleSections
  // (null) or stored an older id set (context / positioning); those show every
  // section. Any array saved by the current Generate / Regenerate modal is
  // authoritative — a section toggled off must stay hidden, even if all body
  // sections were turned off and even when its content exists.
  //
  // IMPORTANT: do NOT add "open_questions" here. It is a *current* section key
  // that the Generate/Regenerate flows legitimately save into visibleSections.
  // Treating it as a legacy marker made every proposal with open questions
  // enabled fall into "show everything" mode, so the toggles silently did
  // nothing. Only keys that exist *exclusively* in the old schema belong here.
  const LEGACY_MARKERS = ["context", "positioning"];
  const isLegacyVisibleSections =
    Array.isArray(visibleSections) &&
    visibleSections.some((id) => LEGACY_MARKERS.includes(id));
  const showSection = (id: string) => {
    if (!visibleSections) return true;
    if (isLegacyVisibleSections) return true;
    return visibleSections.includes(id);
  };

  const bodyData = variables as ProposalBodyData;
  // Needs / Case studies / Next steps are optional: the AI can legitimately
  // return them empty (e.g. no credible case-study quotes), so unlike the core
  // sections — which keep a "—" shell — they only render when they have content.
  const OPTIONAL_BODY_SECTIONS: BodySectionId[] = ["needs", "case_studies", "next_steps"];
  const activeBodySections = BODY_SECTION_IDS.filter(
    (id) =>
      showSection(id) &&
      (!OPTIONAL_BODY_SECTIONS.includes(id) || hasBodyData(id, bodyData))
  );
  // Brand sections render right after the hero (dashboard / integrations /
  // ai_agent / vision / feedback_loop) — none of them carry a section number
  // anymore, so body sections start at 01.
  const activeBrandSections = BRAND_SECTION_IDS.filter((id) => showSection(id));
  const calculatorNum = String(activeBodySections.length + 1).padStart(2, "0");
  const calculatorVisible = showSection("calculator") && calcState;

  const productCatalog = (p as { productCatalog?: ProductCatalogItem[] }).productCatalog ?? [];
  const perks = (p as { perks?: import("@/components/proposal/perks").Perk[] | null }).perks ?? [];

  return (
    <div className="min-h-screen bg-white text-foreground">
      {showSection("hero") && (
        <ProposalHero
          quoteNumber={quoteNumber}
          validUntil={validUntil}
          preparedAt={preparedAt}
          clientCompany={clientCompany}
          clientPerson={clientPerson}
          clientContacts={clientContacts}
          preparerName={preparerName}
          preparerEmail={preparerEmail}
          language={language}
        />
      )}

      {activeBrandSections.map((id) => {
        const Component = BRAND_SECTION_COMPONENTS[id as BrandSectionId];
        return <Component key={id} num="" language={language} />;
      })}

      <main className="mx-auto max-w-5xl px-5 sm:px-10">
        {activeBodySections.map((id, i) => {
          const Component = BODY_SECTION_COMPONENTS[id as BodySectionId];
          const num = String(i + 1).padStart(2, "0");
          const mainProducts = calcState?.main?.map((m) => ({ name: m.name, qty: m.qty }));
          return (
            <Component
              key={id}
              num={num}
              data={bodyData}
              language={language}
              clientCompany={clientCompany}
              mainProducts={mainProducts}
            />
          );
        })}

        {calculatorVisible && (
          <ProposalCalculatorSection
            state={calcState!}
            language={language}
            catalog={productCatalog}
            perks={perks}
            pinnedExtraIds={pinnedExtraIds}
            onChange={handleCalcChange}
            onAccept={() => setAcceptOpen(true)}
            onQuestion={() => setQuestionOpen(true)}
            onExtraInteract={(event) => {
              if (!token) return;
              recordExtraInterest.mutate({
                token,
                stripeProductId: event.stripe_product_id,
                productName: event.product_name,
                selected: event.selected,
                qty: event.qty,
                unitAmount: event.unit_amount,
                action: event.action,
              });
            }}
            onDownloadPdf={() => {
              if (!calcState) return;
              const pdfOpts = {
                state: calcState,
                catalog: productCatalog,
                language,
                meta: {
                  quoteNumber,
                  preparedAt,
                  validUntil,
                  clientCompany,
                  clientPerson,
                  preparerName,
                  preparerEmail,
                },
              };
              downloadQuotePdf(pdfOpts).catch(() => {
                toast.error(
                  language === "lt"
                    ? "Nepavyko paruošti dokumento. Bandykite dar kartą."
                    : "Could not prepare the document. Please try again."
                );
              });
              if (token) {
                generateQuotePdfBase64(pdfOpts)
                  .then(({ base64, fileName }) => {
                    recordEvent.mutate({
                      token,
                      eventType: "pdf_downloaded",
                      fileBase64: base64,
                      fileName,
                    });
                  })
                  .catch(() => {
                    recordEvent.mutate({ token, eventType: "pdf_downloaded" });
                  });
              }
            }}
            num={calculatorNum}
            clientCompany={clientCompany}
            isAccepted={isAccepted}
            recommendedData={showSection("recommended") ? bodyData : null}
          />
        )}

        {isAccepted && <ProposalAcceptedNotice language={language} />}
      </main>

      {(() => {
        const openQuestions = (p as { openQuestions?: string[] }).openQuestions ?? [];
        return showSection("open_questions") && openQuestions.length > 0 ? (
          <ProposalOpenQuestionsSection questions={openQuestions} language={language} />
        ) : null;
      })()}

      {feedbackFormEnabled && (
        <ProposalFeedbackSection
          onSubmit={(input) => {
            if (!token) return;
            submitFeedback.mutate({ token, ...input });
          }}
          isPending={submitFeedback.isPending}
          language={language}
          defaultName={clientPerson ?? undefined}
          defaultEmail={client.email}
        />
      )}

      {showSection("footer") && (
        <ProposalFooter
          clientCompany={clientCompany}
          preparerName={preparerName}
          preparerEmail={preparerEmail}
          language={language}
        />
      )}

      <AcceptProposalDialog
        open={acceptOpen}
        onOpenChange={setAcceptOpen}
        calcState={calcState}
        catalog={productCatalog}
        onConfirm={({ requirements }) => {
          if (!token) return;
          acceptProposal.mutate({
            token,
            calculatorState: calcState ?? undefined,
            requirements,
          });
        }}
        isPending={acceptProposal.isPending}
        isAccepted={isAccepted}
        language={language}
      />

      <AskQuestionDialog
        open={questionOpen}
        onOpenChange={setQuestionOpen}
        onSubmit={(input) => {
          if (!token) return;
          askQuestion.mutate({ token, ...input });
        }}
        isPending={askQuestion.isPending}
        language={language}
        defaultEmail={client.email}
      />
    </div>
  );
}
